import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ExternalLink, Copy, Send, Loader2 } from "lucide-react";
import { fmtDateOrDash } from "../clientConstants";

const STATUS_LABEL = { not_started: "Not started", in_progress: "In progress", ready: "Ready to deliver", sent: "Delivered" };

export function DeliveriesPanel({ clientId, onOpenOrder }) {
  const [deliveries, setDeliveries] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  useEffect(() => { api.get(`/admin/clients/${clientId}/deliveries`).then((r) => setDeliveries(r.data)).catch(() => setDeliveries([])); }, [clientId]);
  if (deliveries === null) return <div className="text-sm text-slate-400 py-10 text-center">Loading deliveries…</div>;
  if (!deliveries.length) return <div data-testid="client-deliveries-empty" className="text-sm text-slate-400 py-10 text-center border border-dashed border-slate-200 rounded-lg">No property websites yet for this client.</div>;
  const copyLink = (slug) => { navigator.clipboard.writeText(`${window.location.origin}/listing/${slug}`); toast.success("Property website link copied"); };
  const markDelivered = async (bookingId) => {
    setSendingId(bookingId);
    try {
      const r = await api.post(`/admin/orders/${bookingId}/delivery/send`);
      const attempt = (r.data.delivery?.email_attempts || []).slice(-1)[0];
      const status = r.data.delivery?.status;
      if (attempt?.status === "failed") toast.error(`Delivery email failed to send to: ${attempt.failed.join(", ")}`);
      else if (attempt?.status === "partial") toast.warning(`Sent, but failed for: ${attempt.failed.join(", ")}`);
      else toast.success("Property website marked as delivered — client notified");
      setDeliveries((rows) => rows.map((r2) => (r2.booking_id === bookingId ? { ...r2, status: status || r2.status } : r2)));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSendingId(null); }
  };
  return (
    <div data-testid="client-deliveries-panel" className="space-y-3">
      {deliveries.map((d) => (
        <div key={d.booking_id} data-testid={`client-delivery-row-${d.booking_id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E4E9F0] px-4 py-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#101828] truncate">{d.address || d.project_name || `Job #${d.job_number}`}</div>
            <div className="text-xs text-[#475467] mt-0.5">{STATUS_LABEL[d.status] || d.status} · Created {fmtDateOrDash(d.created_at)}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {d.gallery_slug && d.website_published && (
              <>
                <a data-testid={`client-delivery-view-${d.booking_id}`} href={`/listing/${d.gallery_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0C2347] hover:underline"><ExternalLink size={13} /> View website</a>
                <Button data-testid={`client-delivery-copy-${d.booking_id}`} variant="ghost" size="sm" className="rounded-lg text-xs gap-1.5" onClick={() => copyLink(d.gallery_slug)}><Copy size={13} /> Copy link</Button>
              </>
            )}
            {d.status !== "sent" && (
              <Button
                data-testid={`client-delivery-mark-delivered-${d.booking_id}`}
                variant="outline" size="sm"
                className="rounded-lg text-xs gap-1.5 border-[#087443]/30 text-[#087443] hover:bg-[#087443]/5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                disabled={sendingId === d.booking_id || !d.website_published}
                title={!d.website_published ? "Publish the property website from the job's Website tab before sending" : undefined}
                onClick={() => markDelivered(d.booking_id)}
              >
                {sendingId === d.booking_id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Mark as delivered
              </Button>
            )}
            <Button data-testid={`client-delivery-open-${d.booking_id}`} variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => onOpenOrder(d.booking_id, "media")}>Open job</Button>
          </div>
        </div>
      ))}
    </div>
  );
}
